import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Link2, TrendingUp, IndianRupee, Instagram, ArrowRight } from "lucide-react";
import { API, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";

function Spotlight() {
  const [creators, setCreators] = useState(null);
  useEffect(() => { API.get("/creators/spotlight").then((r) => setCreators(r.data)).catch(() => setCreators([])); }, []);
  if (!creators || creators.length === 0) return null;
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-8" data-testid="creator-spotlight">
      <div className="text-center">
        <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#F6A5BE]">Creator Spotlight</p>
        <h2 className="font-display text-3xl sm:text-4xl font-semibold mt-2">Shop Her Picks.</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
        {creators.map((c) => (
          <div key={c.ref} className="bg-white text-stone-900 rounded-2xl overflow-hidden shadow-warm" data-testid={`spotlight-${c.ref}`}>
            <div className="flex items-center gap-3 p-5">
              <span className="w-12 h-12 rounded-full bg-[#6A1B29] text-white grid place-items-center font-display text-xl font-semibold overflow-hidden shrink-0">
                {c.avatar ? <img src={c.avatar} alt={c.handle} className="w-full h-full object-cover" /> : (c.handle?.replace(/^@/, "")[0]?.toUpperCase() || "S")}
              </span>
              <div className="min-w-0">
                <p className="font-display text-lg font-semibold truncate">{c.handle || c.name}</p>
                {c.instagram && <a href={c.instagram.startsWith("http") ? c.instagram : `https://instagram.com/${c.instagram.replace(/^@/, "")}`} target="_blank" rel="noreferrer" className="text-xs text-[#6A1B29] inline-flex items-center gap-1"><Instagram className="w-3 h-3" /> Instagram</a>}
              </div>
            </div>
            {c.bio && <p className="px-5 -mt-1 text-sm text-stone-500 line-clamp-2">{c.bio}</p>}
            {c.picks?.length > 0 ? (
              <div className="grid grid-cols-4 gap-1.5 p-3 mt-2">
                {c.picks.map((p) => (
                  <Link key={p.product_id} to={`/product/${p.slug || p.product_id}?ref=${c.ref}`} className="aspect-square rounded-lg overflow-hidden bg-[#F9ECE8]" data-testid={`spotlight-pick-${p.product_id}`}>
                    {p.image && <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform" />}
                  </Link>
                ))}
              </div>
            ) : <div className="p-3" />}
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Creators() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [f, setF] = useState({ handle: "", bio: "", instagram: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user) API.get("/creators/me").then((r) => setMe(r.data)).catch(() => {}); }, [user]);

  const apply = async () => {
    if (!user) { nav("/login", { state: { from: "/creators" } }); return; }
    if (!f.handle.trim()) { toast.error("Add your creator handle"); return; }
    setBusy(true);
    try { const { data } = await API.post("/creators/apply", f); setMe(data); toast.success("Application submitted 💗"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const FIELD = "w-full border border-stone-300 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#6A1B29]";
  return (
    <div className="bg-[#2A1418] text-white min-h-[70vh]" data-testid="creators-page">
      <Seo title="Suvikka Creator Club | Love It. Share It. Earn From It." description="Become a Suvikka Creator — create content around products you genuinely love and earn eligible commissions from qualifying sales." canonicalPath="/creators" />
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
        <span className="inline-flex p-3 rounded-2xl bg-white/10 text-[#F6A5BE]"><Camera className="w-7 h-7" /></span>
        <h1 className="font-display text-4xl sm:text-6xl font-semibold mt-5">Love It. Share It.<br /><span className="text-[#F6A5BE] italic">Earn From It.</span></h1>
        <p className="text-stone-300 mt-4 max-w-xl mx-auto text-lg">Create content around Suvikka products you genuinely love and earn eligible commissions from qualifying sales.</p>
      </section>
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[[Link2, "Tracking Links", "Unique links for products you promote."], [TrendingUp, "Real Dashboard", "Clicks, orders and commissions."], [IndianRupee, "Get Paid", "Eligible commission on qualifying sales."]].map(([Icon, t, d], i) => (
          <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid={`creator-benefit-${i}`}>
            <Icon className="w-6 h-6 text-[#F6A5BE]" /><p className="font-semibold mt-3">{t}</p><p className="text-sm text-stone-400 mt-1">{d}</p>
          </div>
        ))}
      </section>
      <Spotlight />
      <section className="max-w-lg mx-auto px-4 sm:px-6 py-10 pb-24">
        <div className="bg-white text-stone-900 rounded-2xl p-6 shadow-warm">
          {me ? (
            <div className="text-center" data-testid="creator-status">
              <p className="font-display text-2xl font-semibold">You're {me.status === "approved" ? "an approved Creator 💗" : "in review"}</p>
              <p className="text-stone-500 mt-2 text-sm">Status: <span className="font-semibold capitalize">{me.status}</span> · Ref {me.ref} · {me.commission_percent}% commission</p>
              {me.status === "approved" && <button onClick={() => nav("/creator/dashboard")} className="mt-5 bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold px-6 py-3 rounded-full inline-flex items-center gap-2" data-testid="go-dashboard">Go to Dashboard <ArrowRight className="w-4 h-4" /></button>}
            </div>
          ) : (
            <div className="grid gap-3" data-testid="creator-apply-form">
              <h3 className="font-display text-xl font-semibold">Become a Suvikka Creator</h3>
              <input placeholder="Creator handle e.g. @yourname" value={f.handle} onChange={(e) => setF({ ...f, handle: e.target.value })} className={FIELD} data-testid="creator-handle" />
              <input placeholder="Instagram (optional)" value={f.instagram} onChange={(e) => setF({ ...f, instagram: e.target.value })} className={FIELD} data-testid="creator-instagram" />
              <textarea placeholder="Tell us about your content (optional)" rows={2} value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} className={FIELD} data-testid="creator-bio" />
              <button onClick={apply} disabled={busy} className="bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold px-6 py-3 rounded-full disabled:opacity-60" data-testid="creator-apply">{busy ? "Submitting…" : "Become a Creator"}</button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
